/**
 * This contains some helpers for fetching from specific API endpoints. After we
 * finish migrating the corresponding views to use GraphQL we should be able to
 * remove this.
 */
/* eslint-disable @typescript-eslint/no-floating-promises */
import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useShowMessage } from '../../core-ui';

import IndexedList from '../../support/shared/IndexedList';
import normalizePriceTiers from '../../support/shared/normalizePriceTiers';
import { Addon } from '../../types/shared/Addon';
import { SignupFormDefinition } from '../../types/shared/FormDefinition';
import { Market } from '../../types/shared/Market';
import { Tag } from '../../types/shared/Tag';
import { Vendor } from '../../types/shared/Vendor';
import { useFetchJson } from '../../support/useFetch';

type HookOptions = {
  onError?: (error: unknown) => void;
};

// TODO: Remove this or refactor. Should use a single async fn that fetches resources in parallel.
function useQueries<T extends Record<string, unknown>>(
  input: { [K in keyof T]: UseQueryResult<T[K], unknown> },
): {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  refetchAll: () => void;
} {
  return useMemo(() => {
    let data: Record<string, unknown> = {};
    let isDataIncomplete = false;
    let isLoading = false;
    let error = null;
    const retryFns: Array<() => void> = [];
    for (const [key, result] of Object.entries(input)) {
      retryFns.push(result.refetch);
      data[key] = result.data;
      if (result.data == null) {
        isDataIncomplete = true;
      }
      isLoading = isLoading || result.isLoading;
      error = error || result.error;
    }
    const refetchAll = () => retryFns.forEach((fn) => fn());
    if (isDataIncomplete) {
      return { data: null, isLoading, error, refetchAll };
    } else {
      return { data: data as T, isLoading, error, refetchAll };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, Object.values(input));
}

export function useQueryVendorDetails(vendorId: string) {
  const fetchJson = useFetchJson();

  return useQueries({
    vendor: useQuery(['vendor/:id', vendorId], () =>
      fetchJson(`/api/vendors/${vendorId}`, null, (data) => {
        const { fields, ...other } = data.vendor;
        return { ...other, fields: new IndexedList(fields) } as Vendor;
      }),
    ),
    addons: useQuery(['addons'], () =>
      fetchJson(`/api/addons`, null, (data) => data.addons as Array<Addon>),
    ),
    markets: useQuery(['markets'], () =>
      fetchJson(`/api/markets`, null, (data) => data.markets as Array<Market>),
    ),
    formDef: useQuery(['forms/signup'], () =>
      fetchJson<SignupFormDefinition>(`/api/forms/signup`),
    ),
    priceTiers: useQuery(['pricing'], () =>
      fetchJson(`/api/pricing`, null, (data) => {
        return normalizePriceTiers(data.tiers);
      }),
    ),
    tags: useQuery(['tags'], () =>
      fetchJson(`/api/tags`, null, (data) => data.tags as Array<Tag>),
    ),
  });
}

export function useQueryExportDetails(options: HookOptions = {}) {
  const fetchJson = useFetchJson();

  return useQueries({
    markets: useQuery(
      ['markets'],
      () =>
        fetchJson(
          `/api/markets`,
          null,
          (data) => data.markets as Array<Market>,
        ),
      options,
    ),
    formDef: useQuery(
      ['forms/signup'],
      () => fetchJson<SignupFormDefinition>(`/api/forms/signup`),
      options,
    ),
    tags: useQuery(
      ['tags'],
      () => fetchJson(`/api/tags`, null, (data) => data.tags as Array<Tag>),
      options,
    ),
  });
}

export function useQueryTags(options: HookOptions = {}) {
  const fetchJson = useFetchJson();

  const { data, ...other } = useQuery(
    ['tags'],
    () => fetchJson(`/api/tags`, null, (data) => data.tags as Array<Tag>),
    options,
  );

  return [data, other] as const;
}

export function useSaveVendor(vendorId: string) {
  const fetchJson = useFetchJson();
  const queryClient = useQueryClient();
  const showMessage = useShowMessage();
  const { mutate, ...other } = useMutation(
    ['vendor/:id', vendorId],
    async (vendor: Vendor) => {
      const data = await fetchJson(`/api/vendors/${vendorId}`, {
        method: 'POST',
        body: vendor,
      });
      return { success: Boolean(data?.success) };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['vendor/:id', vendorId]);
        showMessage('success', t('Saved successfully'));
      },
      onError: (error) => {
        showMessage('error', t('Error saving vendor: {error}', { error }));
      },
    },
  );
  return [mutate, other] as const;
}
